import { Button } from "@designs-by-maryam/ui/components/button";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";

/**
 * Route-level error component for TanStack Router.
 *
 * Handles errors that occur during route loading or rendering.
 * Provides user-friendly error message and recovery options.
 *
 * This component receives {error, reset} props from TanStack Router
 * and is registered via the `errorComponent` route option.
 *
 * @see https://tanstack.com/router/latest/docs/framework/react/guide/data-loading#the-routeoptionserrorcomponent-option
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
	const router = useRouter();

	return (
		<div
			aria-live="assertive"
			className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4"
			role="alert"
		>
			<div className="text-center">
				<h2 className="mb-2 font-semibold text-2xl text-foreground">
					Page Error
				</h2>
				<p className="max-w-md text-muted-foreground">
					This page encountered an error. Try navigating to a different page or
					refreshing.
				</p>
			</div>

			{/* Only show error details in development */}
			{import.meta.env.DEV && (
				<details className="max-w-lg rounded-md border bg-muted/50 p-4 text-left">
					<summary className="cursor-pointer font-medium text-sm">
						Error Details
					</summary>
					<pre className="mt-2 overflow-auto whitespace-pre-wrap text-destructive text-xs">
						{error.message}
						{error.stack && `\n\n${error.stack}`}
					</pre>
				</details>
			)}

			<div className="flex gap-4">
				<Button onClick={() => window.history.back()} variant="outline">
					Go Back
				</Button>
				<Button
					onClick={() => {
						// Reset the error boundary and invalidate the route to retry
						reset();
						router.invalidate();
					}}
				>
					Try Again
				</Button>
			</div>
		</div>
	);
}
